import React, { useState, useEffect, useRef, useCallback } from 'react';
import cityCodesData from '../../constant/city_codes.json';
import RegionDropdownMetrix from '../RegionDropdownMetrix';
import { useFilterContext } from '../../context/aptmetrix/FilterContext';

import MoveInDateFilter from './MoveInDateFilter';
import AreaFilter from './AreaFilter';
import HouseholdFilter from './HouseholdFilter';
import PriceFilter from './PriceFilter';
import GapFilter from './GapFilter';
import SubdistrictFilter from './SubdistrictFilter';
import SchoolFilter from './SchoolFilter';
import SubwayFilter from './SubwayFilter';
import WalkingTimeFilter from './WalkingTimeFilter';
import GangnamTimeFilter from './GangnamTimeFilter';

const FilterBar = ({
    data,
    onRegionSelect,
    onPriceFilterChange,
    onHouseholdFilterChange,
    onAreaFilterChange,
    onGapFilterChange,
    onMoveInDateFilterChange,
    onSubdistrictFilterChange,
    onSubwayFilterChange,
    onGangnamTimeFilterChange,
    onSchoolFilterChange,
}) => {
    const {
        filterUIState,
        setFilterUIState,
        filterComponentState,
        setFilterComponentState,
        setPriceFilter,
        setHouseholdFilter,
        setAreaFilter,
        setGapFilter,
        setMoveInDateFilter,
        setSubdistrictFilter,
        setSubwayFilter,
        setGangnamTimeFilter,
        setSchoolFilter,
    } = useFilterContext();

    const { selectedRegion, subdistrictOptions, subwayLines } = filterComponentState;
    const selectedCities = filterComponentState.selectedCities;

    const setSelectedRegion = (value) => setFilterComponentState(prev => ({ ...prev, selectedRegion: value }));
    const setSelectedCities = (value) => setFilterComponentState(prev => ({ ...prev, selectedCities: value }));
    const setSubdistrictOptions = (value) => setFilterComponentState(prev => ({ ...prev, subdistrictOptions: value }));
    const setSubwayLines = (value) => setFilterComponentState(prev => ({ ...prev, subwayLines: value }));

    const [siDoData, setSiDoData] = useState(null);
    const [siGunGuData, setSiGunGuData] = useState(null);
    const [activeTooltip, setActiveTooltip] = useState(null);

    // Tooltip coordination
    const toggleTooltip = useCallback((name) => {
        setActiveTooltip(prev => prev === name ? null : name);
    }, []);

    const closeTooltip = useCallback(() => {
        setActiveTooltip(null);
    }, []);

    // Region data initialization
    useEffect(() => {
        const uniqueRegions = [
            ...[...new Map(cityCodesData.map(item => [item['지역명'], item['지역코드']])).values()]
                .map(code => ({
                    cortarName: cityCodesData.find(item => item['지역코드'] === code)['지역명'],
                    cortarNo: code,
                }))
        ];
        setSiDoData(uniqueRegions);
    }, []);

    useEffect(() => {
        if (selectedRegion) {
            const cities = [
                ...cityCodesData
                    .filter(item => item['지역코드'] === selectedRegion)
                    .map(item => ({ cortarName: item['시군구명'], cortarNo: item['시군구코드'] }))
            ];
            setSiGunGuData([...new Map(cities.map(city => [city.cortarNo, city])).values()]);
        }
    }, [selectedRegion]);

    // Data processing for subdistrict/subway options
    const prevDataHashRef = useRef(null);

    useEffect(() => {
        if (data && Array.isArray(data) && data.length > 0) {
            const currentDataHash = JSON.stringify({
                length: data.length,
                firstComplexNo: data[0]?.complex_no,
                lastComplexNo: data[data.length - 1]?.complex_no,
                complexNos: data.slice(0, Math.min(5, data.length)).map(item => item.complex_no).join(','),
            });

            const isNewData = prevDataHashRef.current !== currentDataHash;

            const uniqueSubdistricts = [...new Set(data.map(item => item.subdistrict_name))]
                .filter(name => name)
                .sort();

            setSubdistrictOptions(uniqueSubdistricts);

            if (isNewData && !filterUIState.isSubdistrictFilterActive && filterUIState.selectedSubdistricts.length === 0) {
                setFilterUIState(prev => ({ ...prev, selectedSubdistricts: uniqueSubdistricts }));
            }

            collectSubwayStations(data);
            prevDataHashRef.current = currentDataHash;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const collectSubwayStations = (items) => {
        const lineMap = new Map();

        items.forEach(item => {
            if (item.subwaystationname && item.subwaystationname.trim() !== '' &&
                item.subwaylinename && item.subwaylinename.trim() !== '') {
                const stationNames = item.subwaystationname.split(',');
                const lineNames = item.subwaylinename.split(',');
                const lineColors = item.subwaylinecolor ? item.subwaylinecolor.split(',') : [];

                stationNames.forEach((stationName, index) => {
                    const station = stationName.trim();
                    const lines = lineNames[index] ? lineNames[index].split('|') : [];
                    const colors = lineColors[index] ? lineColors[index].split('|') : [];

                    lines.forEach((line, i) => {
                        const lineName = line.trim();
                        const lineColor = colors[i] ? colors[i].trim() : '#888';

                        if (lineMap.has(lineName)) {
                            const lineInfo = lineMap.get(lineName);
                            if (!lineInfo.stations.includes(station)) {
                                lineInfo.stations = [...lineInfo.stations, station];
                            }
                        } else {
                            lineMap.set(lineName, {
                                name: lineName,
                                color: lineColor,
                                stations: [station],
                            });
                        }
                    });
                });
            }
        });

        const lineList = Array.from(lineMap.values()).sort((a, b) => {
            const numA = parseInt(a.name.replace(/[^0-9]/g, ''));
            const numB = parseInt(b.name.replace(/[^0-9]/g, ''));
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.name.localeCompare(b.name, 'ko-KR');
        });

        setSubwayLines(lineList);
    };

    // Region handlers
    const handleRegionChange = (e) => {
        const regionValue = e.target.value;
        setSelectedRegion(regionValue);
        setSelectedCities([]);

        const regionName = siDoData?.find(r => r.cortarNo === regionValue)?.cortarName || '';
        const regionData = {
            regionCode: regionValue,
            cityCode: '',
            regionName,
            cityName: '',
        };

        onRegionSelect(regionData);

        if (e.userInitiated !== false) {
            resetAllFilters();
        }

        if (e.autoSearch && regionValue) {
            triggerSearch(regionData);
        }
    };

    const handleCityChange = (e) => {
        if (typeof e.target.value === 'string' && !e.selectedCities) {
            const cityValue = e.target.value;
            const regionName = siDoData?.find(r => r.cortarNo === selectedRegion)?.cortarName || '';
            const cityName = cityValue === '전체' ? '전체' : siGunGuData?.find(c => c.cortarNo === cityValue)?.cortarName || '';

            setSelectedCities(cityValue === '전체' ? [] : [{ cortarNo: cityValue, cortarName: cityName }]);

            const regionData = {
                regionCode: selectedRegion,
                cityCode: cityValue === '전체' ? '' : cityValue,
                regionName,
                cityName,
                multipleSelect: false,
            };

            onRegionSelect(regionData);

            if (e.autoSearch) {
                triggerSearch(regionData);
            }

            return;
        }

        if (e.selectedCities) {
            setSelectedCities(e.selectedCities);

            const regionName = e.regionName || siDoData?.find(r => r.cortarNo === selectedRegion)?.cortarName || '';
            const cityCodes = e.selectedCities.map(city => city.cortarNo);
            const cityNames = e.selectedCities.map(city => city.cortarName);

            const regionData = {
                regionCode: selectedRegion,
                cityCode: cityCodes,
                regionName,
                cityName: cityNames,
                multipleSelect: true,
            };

            onRegionSelect(regionData);

            if (e.autoSearch) {
                triggerSearch(regionData);
            }
        }
    };

    const triggerSearch = (regionData) => {
        const isMultipleSelect = regionData.multipleSelect || false;
        const isMultipleRegions = regionData.multipleRegions || false;

        if ((isMultipleSelect || isMultipleRegions) && Array.isArray(regionData.cityCode)) {
            onRegionSelect({ ...regionData, searchClicked: true });
        } else {
            onRegionSelect({
                ...regionData,
                searchClicked: true,
                cityName: regionData.cityCode === '' && regionData.regionCode !== '' ? '전체' : regionData.cityName,
            });
        }
    };

    // Reset all filters
    const resetAllFilters = () => {
        setFilterUIState(prev => ({
            ...prev,
            minPrice: '', maxPrice: '', isPriceFilterActive: false,
            minHousehold: '', maxHousehold: '', isHouseholdFilterActive: false,
            minArea: '', maxArea: '', isAreaFilterActive: false,
            minGap: '', maxGap: '', isGapFilterActive: false,
            minYears: '', maxYears: '', isMoveInDateFilterActive: false,
            selectedSubdistricts: [...subdistrictOptions],
            isSubdistrictFilterActive: false,
            selectedSubwayLines: [], maxWalkingTime: null,
            isSubwayFilterActive: false, isWalkingTimeFilterActive: false,
            maxGangnamTime: null, isGangnamTimeFilterActive: false,
            hasSchool: true, isSchoolFilterActive: false,
        }));

        setPriceFilter({ minPrice: null, maxPrice: null });
        setHouseholdFilter({ minHousehold: null, maxHousehold: null });
        setAreaFilter({ minArea: null, maxArea: null });
        setGapFilter({ minGap: null, maxGap: null });
        setMoveInDateFilter({ minYears: null, maxYears: null });
        setSubdistrictFilter(null);
        setSubwayFilter(null);
        setGangnamTimeFilter({ maxTime: null });
        setSchoolFilter(null);

        onPriceFilterChange({ minPrice: null, maxPrice: null });
        onHouseholdFilterChange({ minHousehold: null, maxHousehold: null });
        onAreaFilterChange({ minArea: null, maxArea: null });
        onGapFilterChange({ minGap: null, maxGap: null });
        onMoveInDateFilterChange({ minYears: null, maxYears: null });
        onSubdistrictFilterChange(null);
        onSubwayFilterChange(null);
        if (onGangnamTimeFilterChange) onGangnamTimeFilterChange(null);
        if (onSchoolFilterChange) onSchoolFilterChange(null);

        setActiveTooltip(null);
    };

    const {
        isPriceFilterActive, isHouseholdFilterActive, isAreaFilterActive,
        isGapFilterActive, isMoveInDateFilterActive, isSubdistrictFilterActive,
        isSubwayFilterActive, isWalkingTimeFilterActive, isGangnamTimeFilterActive,
        isSchoolFilterActive,
    } = filterUIState;

    const isAnyFilterActive = isPriceFilterActive || isHouseholdFilterActive || isAreaFilterActive ||
        isGapFilterActive || isMoveInDateFilterActive || isSubdistrictFilterActive ||
        isSubwayFilterActive || isWalkingTimeFilterActive || isGangnamTimeFilterActive ||
        isSchoolFilterActive;

    const commonProps = { selectedRegion, data, onClose: closeTooltip };

    return (
        <div className="flex justify-center w-full">
            <section className="flex flex-col box-border m-1 p-1.5 w-full sm:w-[56vw]">
                <div className="flex flex-col md:flex-row justify-around p-2 mb-4 bg-white rounded-lg md:rounded-full shadow-lg">
                    <RegionDropdownMetrix
                        selectedRegion={selectedRegion}
                        selectedCity={selectedCities.length === 1 ? selectedCities[0].cortarNo : ''}
                        onRegionChange={handleRegionChange}
                        onCityChange={handleCityChange}
                        siDoData={siDoData}
                        siGunGuData={siGunGuData}
                    />

                    <div className="grid grid-cols-2 md:flex md:flex-row gap-2 mt-2 md:mt-0">
                        <MoveInDateFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'moveInDate'}
                            onToggle={() => toggleTooltip('moveInDate')}
                            onFilterChange={onMoveInDateFilterChange}
                        />
                        <AreaFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'area'}
                            onToggle={() => toggleTooltip('area')}
                            onFilterChange={onAreaFilterChange}
                        />
                        <HouseholdFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'household'}
                            onToggle={() => toggleTooltip('household')}
                            onFilterChange={onHouseholdFilterChange}
                        />
                        <PriceFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'price'}
                            onToggle={() => toggleTooltip('price')}
                            onFilterChange={onPriceFilterChange}
                        />
                        <GapFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'gap'}
                            onToggle={() => toggleTooltip('gap')}
                            onFilterChange={onGapFilterChange}
                        />
                        <SubdistrictFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'subdistrict'}
                            onToggle={() => toggleTooltip('subdistrict')}
                            onFilterChange={onSubdistrictFilterChange}
                        />
                        <SchoolFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'school'}
                            onToggle={() => toggleTooltip('school')}
                            onFilterChange={onSchoolFilterChange}
                        />
                        <SubwayFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'subway'}
                            onToggle={() => toggleTooltip('subway')}
                            onFilterChange={onSubwayFilterChange}
                        />
                        <WalkingTimeFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'walkingTime'}
                            onToggle={() => toggleTooltip('walkingTime')}
                            onSubwayFilterChange={onSubwayFilterChange}
                        />
                        <GangnamTimeFilter
                            {...commonProps}
                            isOpen={activeTooltip === 'gangnamTime'}
                            onToggle={() => toggleTooltip('gangnamTime')}
                            onFilterChange={onGangnamTimeFilterChange}
                        />

                        {/* Reset all filters button */}
                        <div className="col-span-2 md:col-span-1">
                            <button
                                onClick={resetAllFilters}
                                disabled={!isAnyFilterActive}
                                className={`px-2 py-1 text-xs md:text-sm rounded-full w-full ${
                                    isAnyFilterActive
                                        ? 'bg-orange-500 text-white font-medium hover:bg-red-600'
                                        : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-200 hover:text-gray-600'
                                }`}
                            >
                                필터초기화
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FilterBar;
